import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import api from "../lib/api.js";
import { useDeviceChannel } from "../hooks/useDeviceChannel.js";
import { parseISO } from "date-fns";
import { swissDate, smartDate } from "../lib/dates.js";
import { STATUS_CONFIG } from "../lib/mapStyles.js";

const TIME_WINDOWS = { "24h": 24 * 60 * 60 * 1000, "7D": 7 * 24 * 60 * 60 * 1000, "30D": 30 * 24 * 60 * 60 * 1000 };
const HOURS_MAP = { "24h": 24, "7D": 168, "30D": 720 };

export default function DeviceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState("24h");
  useDeviceChannel(id);

  const { data: device, isLoading, error } = useQuery({
    queryKey: ["device", id],
    queryFn: () => api.get(`devices/${id}`).json(),
  });

  const readingsFrom = new Date(Date.now() - TIME_WINDOWS[timeRange]).toISOString();
  const { data: readings = [] } = useQuery({
    queryKey: ["device-readings", id, timeRange],
    queryFn: () => api.get(`devices/${id}/readings`, { searchParams: { from: readingsFrom } }).json(),
    refetchInterval: 60_000,
  });

  const { data: detections = [] } = useQuery({
    queryKey: ["rave-detections", id],
    queryFn: () => api.get(`rave_detections?device_id=${id}`).json(),
  });

  if (isLoading) {
    return <div className="p-6 data text-sm" style={{ color: "var(--color-muted)" }}>Loading…</div>;
  }

  if (error || !device) {
    return (
      <div className="p-6 space-y-3">
        <button onClick={() => navigate(-1)} className="btn btn-ghost" style={{ padding: "6px 10px", fontSize: 13 }}>← Back</button>
        <div className="data text-sm" style={{ color: "var(--color-red)" }}>
          {error?.message || "Device not found"}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate(-1)}
          className="btn btn-ghost"
          style={{ padding: "6px 10px", fontSize: 13, marginBottom: 8 }}
        >
          ← Back
        </button>
        <h1 className="text-xl font-semibold data" style={{ color: "var(--color-white)" }}>{device?.name}</h1>
        {device?.location_name && (
          <p className="data text-xs mt-0.5" style={{ color: "var(--color-muted)" }}>{device.location_name}</p>
        )}
        {device?.last_seen_at && (
          <p className="data text-xs mt-0.5" style={{ color: "var(--color-muted)" }}>
            Last update: {smartDate(device.last_seen_at)}
          </p>
        )}
      </div>

      {/* Stat row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { key: "Conf",   label: "Confidence",                             value: device?.rave_confidence != null ? `${device.rave_confidence}%` : "—", color: device?.rave_confidence >= device?.detection_threshold * 100 ? "pink" : "cyan" },
          { key: "SPL",    label: "SPL",                                    value: device?.last_reading_spl  != null ? `${(+device.last_reading_spl).toFixed(1)} dB`  : "—", color: (+device?.last_reading_spl  > 70) ? "red" : "cyan" },
          { key: "LEQ",    label: <span>L<sub>EQ</sub></span>,              value: device?.last_reading_leq  != null ? `${(+device.last_reading_leq).toFixed(1)} dB`  : "—", color: (+device?.last_reading_leq  > 70) ? "red" : "cyan" },
          { key: "Lmax",   label: <span>L<sub>max</sub></span>,             value: device?.last_reading_lmax != null ? `${(+device.last_reading_lmax).toFixed(1)} dB` : "—", color: (+device?.last_reading_lmax > 70) ? "red" : "cyan" },
          { key: "Status", label: "Status",                                 value: (STATUS_CONFIG[device?.status] || STATUS_CONFIG.normal).label, cssColor: (STATUS_CONFIG[device?.status] || STATUS_CONFIG.normal).color },
        ].map(({ key, label, value, color, cssColor }) => (
          <div key={key} className="card p-4">
            <div className="data text-xs uppercase mb-2" style={{ color: "var(--color-muted)", letterSpacing: "0.1em" }}>{label}</div>
            <div className={`text-2xl font-bold data ${color ? `neon-${color}` : ""}`} style={cssColor ? { color: cssColor } : undefined}>{value}</div>
          </div>
        ))}
      </div>

      {/* Confidence chart */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="data text-xs uppercase neon-cyan" style={{ letterSpacing: "0.15em" }}>
            Confidence
          </h3>
          <TimeRangePicker value={timeRange} onChange={setTimeRange} />
        </div>
        <DeviceConfidenceTimeline deviceId={id} timeRange={timeRange} detectThreshold={device?.detection_threshold} />
      </div>

      {/* Sound timeline chart */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="data text-xs uppercase neon-cyan" style={{ letterSpacing: "0.15em" }}>
            Sound Level Timeline
          </h3>
          <TimeRangePicker value={timeRange} onChange={setTimeRange} />
        </div>
        <SoundTimeline readings={readings} detections={detections} timeRange={timeRange} measurementFrequency={device?.measurement_frequency || 300} />
      </div>

      {/* Rave detections */}
      <div className="card p-5">
        <h3 className="data text-xs uppercase neon-cyan mb-4" style={{ letterSpacing: "0.15em" }}>
          Rave Detection History
        </h3>
        {detections.length === 0 ? (
          <div className="data text-sm text-center py-6" style={{ color: "var(--color-muted)" }}>
            No detections recorded
          </div>
        ) : (
          <div className="space-y-3">
            {detections.map((d) => (
              <div key={d.id} className="card-raised p-4 rounded">
                <div className="flex items-center justify-between mb-2">
                  <span className="data text-sm" style={{ color: "var(--color-white)" }}>
                    {swissDate(d.started_at)}
                    {d.ended_at && ` → ${swissDate(d.ended_at)}`}
                  </span>
                  <span className={`badge ${d.status === "active" ? "badge-pink pulse-pink" : d.status === "false_positive" ? "badge-muted" : "badge-green"}`}>
                    {d.status.toUpperCase()}
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-4">
                  <Metric label="Peak SPL"   value={d.peak_spl        ? `${(+d.peak_spl).toFixed(1)} dB`             : "—"} />
                  <Metric label="Avg LEQ"    value={d.average_leq     ? `${(+d.average_leq).toFixed(1)} dB`           : "—"} />
                  <Metric label="Confidence" value={d.confidence_score ? `${(d.confidence_score * 100).toFixed(1)}%`  : "—"} />
                  <Metric label="Duration"   value={formatDuration(d.duration_seconds)} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function formatDuration(seconds) {
  if (seconds == null || seconds <= 0) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${seconds}s`;
}

function Metric({ label, value }) {
  return (
    <div>
      <div className="data text-xs" style={{ color: "var(--color-muted)" }}>{label}</div>
      <div className="data text-sm" style={{ color: "var(--color-cyan)" }}>{value}</div>
    </div>
  );
}

function TimeRangePicker({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {["24h", "7D", "30D"].map((r) => (
        <button
          key={r}
          onClick={() => onChange(r)}
          className="data text-xs"
          style={{
            padding: "3px 8px", borderRadius: 4, cursor: "pointer",
            fontFamily: "var(--font-mono)", letterSpacing: "0.06em",
            border: `1px solid ${value === r ? "var(--color-cyan)" : "var(--color-border)"}`,
            background: value === r ? "rgba(0,245,255,0.1)" : "transparent",
            color: value === r ? "var(--color-cyan)" : "var(--color-muted)",
            transition: "all 0.15s",
          }}
        >
          {r}
        </button>
      ))}
    </div>
  );
}

function DeviceConfidenceTimeline({ deviceId, timeRange, detectThreshold }) {
  const hours = HOURS_MAP[timeRange] || 24;
  const { data, isLoading } = useQuery({
    queryKey: ["confidence-history", deviceId, hours],
    queryFn: () => api.get(`devices/${deviceId}/confidence_history`, { searchParams: { hours } }).json(),
    staleTime: 60_000,
  });

  const isMobile = window.innerWidth < 768;

  if (isLoading) return <div className="data text-xs" style={{ color: "var(--color-muted)" }}>Loading chart…</div>;
  if (!data?.history?.length) return <div className="data text-sm text-center py-6" style={{ color: "var(--color-muted)" }}>No confidence data</div>;

  const series = data.history.map((p) => [parseISO(p.timestamp).getTime(), p.confidence]);
  const threshold = data.detect_threshold ?? detectThreshold ?? 0.65;

  const options = {
    chart: {
      backgroundColor: "transparent",
      type: "areaspline",
      style: { fontFamily: "var(--font-mono)" },
      height: isMobile ? 180 : 280,
      marginLeft: 72,
    },
    title: { text: null },
    xAxis: {
      type: "datetime",
      min: Date.now() - hours * 60 * 60 * 1000,
      max: Date.now(),
      lineColor: "var(--color-border)",
      tickColor: "var(--color-border)",
      dateTimeLabelFormats: { hour: "%H:%M", day: "%d.%m" },
      labels: { style: { color: "var(--color-muted)", fontSize: "11px" } },
    },
    yAxis: {
      title: { text: "Confidence", style: { color: "var(--color-muted)", fontSize: "11px" } },
      min: 0,
      max: 1,
      tickInterval: 0.25,
      gridLineColor: "rgba(30,30,46,0.8)",
      labels: {
        style: { color: "var(--color-muted)", fontSize: isMobile ? "10px" : "12px" },
        formatter: function () { return Math.round(this.value * 100) + "%"; },
      },
      plotLines: [{
        value: threshold,
        color: "var(--color-pink)",
        width: 2,
        dashStyle: "Dash",
        zIndex: 5,
        label: {
          text: `Threshold ${(threshold * 100).toFixed(0)}%`,
          align: "right",
          style: { color: "var(--color-pink)", fontSize: "10px", fontFamily: "var(--font-mono)" },
          x: -4, y: -4,
        },
      }],
    },
    legend: { enabled: false },
    tooltip: {
      backgroundColor: "rgba(10,10,15,0.95)",
      borderColor: "var(--color-border)",
      style: { color: "var(--color-white)", fontFamily: "var(--font-mono)" },
      xDateFormat: "%d.%m.%Y %H:%M",
      pointFormatter: function () { return "Confidence: <b>" + Math.round(this.y * 100) + "%</b>"; },
    },
    series: [{
      name: "Confidence",
      data: series,
      color: "#4488ff",
      fillColor: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [0, "rgba(68,136,255,0.25)"],
          [1, "rgba(68,136,255,0.02)"],
        ],
      },
      lineWidth: 1.5,
      marker: { enabled: false },
      threshold: threshold,
      negativeColor: "#4488ff",
      negativeFillColor: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [0, "rgba(68,136,255,0.25)"],
          [1, "rgba(68,136,255,0.02)"],
        ],
      },
      zones: [
        { value: threshold, color: "#4488ff" },
        { color: "#ff3399" },
      ],
    }],
    credits: { enabled: false },
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
}

// Insert a null between consecutive points that are further apart than gapThresholdMs,
// so connectNulls:false breaks the line reliably without relying on Highcharts gapSize.
function withGaps(data, gapThresholdMs) {
  const out = [];
  for (let i = 0; i < data.length; i++) {
    if (i > 0 && data[i][0] - data[i - 1][0] > gapThresholdMs) {
      out.push([data[i - 1][0] + 1, null]);
    }
    out.push(data[i]);
  }
  return out;
}

function SoundTimeline({ readings, detections = [], timeRange = "24h", measurementFrequency = 300 }) {
  if (!readings.length) {
    return (
      <div className="data text-sm text-center py-10" style={{ color: "var(--color-muted)" }}>
        No readings available
      </div>
    );
  }

  const sorted = [...readings]
    .sort((a, b) => parseISO(a.recorded_at) - parseISO(b.recorded_at));
  const times = sorted.map((r) => parseISO(r.recorded_at).getTime());

  const isMobile = window.innerWidth < 768;
  const windowStart = Date.now() - TIME_WINDOWS[timeRange];
  const hasDataInWindow = times.some((t) => t >= windowStart);

  const options = {
    chart: {
      backgroundColor: "transparent",
      type: "spline",
      style: { fontFamily: "var(--font-mono)" },
      height: isMobile ? 260 : 480,
      marginLeft: 48,
    },
    title: { text: null },
    xAxis: {
      type: "datetime",
      min: hasDataInWindow ? windowStart : null,
      max: Date.now(),
      lineColor: "var(--color-border)",
      tickColor: "var(--color-border)",
      dateTimeLabelFormats: {
        millisecond: "%H:%M:%S",
        second:      "%H:%M:%S",
        minute:      "%H:%M",
        hour:        "%H:%M",
        day:         "%d.%m",
        week:        "%d.%m",
        month:       "%m.%Y",
        year:        "%Y",
      },
      labels: { style: { color: "var(--color-muted)", fontSize: "11px" } },
      plotBands: detections
        .filter((d) => d.status === "active" || d.status === "resolved")
        .map((d) => ({
          from: parseISO(d.started_at).getTime(),
          to: d.ended_at ? parseISO(d.ended_at).getTime() : Date.now(),
          color: "rgba(255,51,153,0.08)",
          borderColor: "rgba(255,51,153,0.3)",
          borderWidth: 1,
          label: {
            text: "RAVE",
            style: { color: "rgba(255,51,153,0.7)", fontSize: "10px", fontFamily: "var(--font-mono)" },
            y: 16,
          },
        })),
    },
    yAxis: {
      title: { text: "dB", style: { color: "var(--color-muted)" } },
      gridLineColor: "rgba(30,30,46,0.8)",
      labels: { style: { color: "var(--color-muted)", fontSize: "11px" } },
      min: 0,
      startOnTick: false,
      endOnTick: false,
    },
    legend: {
      itemStyle: { color: "var(--color-muted)", fontFamily: "var(--font-mono)", fontSize: "11px" },
    },
    tooltip: {
      backgroundColor: "rgba(10,10,15,0.95)",
      borderColor: "var(--color-border)",
      style: { color: "var(--color-white)", fontFamily: "var(--font-mono)" },
      xDateFormat: "%d.%m.%Y %H:%M",
    },
    series: (() => {
      const gapMs = measurementFrequency * 3000;
      const rawSpl  = sorted.map((r, i) => [times[i], r.spl  != null ? +r.spl  : null]);
      const rawLeq  = sorted.map((r, i) => [times[i], r.leq  != null ? +r.leq  : null]);
      const rawLmax = sorted.map((r, i) => [times[i], r.lmax != null ? +r.lmax : null]);
      return [
        { name: "SPL",  data: withGaps(rawSpl,  gapMs), color: "#4488ff", lineWidth: 1,   connectNulls: false },
        { name: "LEQ",  data: withGaps(rawLeq,  gapMs), color: "#7a7a9a", lineWidth: 1,   connectNulls: false },
        { name: "Lmax", data: withGaps(rawLmax, gapMs), color: "#ff3399", lineWidth: 1,   connectNulls: false },
      ];
    })(),
    credits: { enabled: false },
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
}
